import objectPath from 'object-path'
import Swal from 'sweetalert2'
import {
  PRINT_PATH,
  ROUTE_HIS,
} from 'designer/component/action/Config'

const defaultAlertConfig = {
  icon: 'success',
  reverseButtons: false,
}

const defaultButtonConfigByKey = {
  confirmButton: {
    enabled: true,
    text: 'Confirm',
    color: '#7367f0',
    icon: 'check',
    iconSource: 'feather',
    actionType: 'closeAlert',
    propAlert: [],
  },
  cancelButton: {
    enabled: false,
    text: 'Cancel',
    color: '#d33',
    icon: 'x',
    iconSource: 'feather',
    actionType: 'closeAlert',
    propAlert: [],
  },
}

const getRuleSweetAlertConfig = (action = {}) => action.successModal || {}

const getRuleSweetAlertAlert = (successModal = {}) => ({
  ...defaultAlertConfig,
  ...(successModal.alert || {}),
})

const getRuleSweetAlertButton = (successModal = {}, buttonKey) => ({
  ...(defaultButtonConfigByKey[buttonKey] || {}),
  ...(successModal[buttonKey] || {}),
})

const escapeHtml = (value = '') => {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const escapeCssValue = (value = '') => {
  return String(value || '').replace(/[;"<>]/g, '')
}

const sanitizeIconName = (value = '') => {
  return String(value || '').replace(/[^\w-]/g, '')
}

const getButtonIconClassName = (button = {}) => {
  let icon = sanitizeIconName(button.icon)
  if (!icon) return ''

  if (button.iconSource === 'fas') return `fas fa-${icon}`
  if (button.iconSource === 'ckd') return `ckd-icon ckd-${icon}`
  if (button.iconSource === 'healthicons') {
    return `healthicons-${icon}-outline`
  }
  return `feather icon-${icon}`
}

const getButtonText = (button = {}) => {
  let iconClassName = getButtonIconClassName(button)
  let text = escapeHtml(button.text || '')

  if (!iconClassName) return text
  return `<i class="${iconClassName} mr-1"></i>${text}`
}

const getButtonLinkType = (button = {}) => {
  if (['page', 'form', 'print', 'url'].includes(button.linkType)) {
    return button.linkType
  }

  if (button.sourceType && ['page', 'form'].includes(button.sourceType)) {
    return button.sourceType
  }

  return 'url'
}

const getDatasourceValue = (designContext = {}, data = {}) => {
  return (
    data.datasourceValue ||
    data.datasource ||
    data.datasources ||
    objectPath.get(designContext, '@datasource') ||
    designContext.datasourceValue ||
    {}
  )
}

const getSourceData = (source, designContext = {}, data = {}) => {
  let { jsonForm = {}, user = {}, urlParams = {} } = designContext
  let formData = data.formData || jsonForm.formData || {}

  switch (source) {
    case 'datasource':
    case 'datasources':
      return getDatasourceValue(designContext, data)
    case 'response':
      return data.response || objectPath.get(formData, '@response') || {}
    case 'urlParam':
      return data.urlParams || urlParams || {}
    case 'user':
      return user || {}
    case 'formData':
      return formData
    case 'value':
    default:
      return null
  }
}

const getConfigValue = (config = {}, designContext = {}, data = {}) => {
  let source = config.source || config.systemType || config.propType || 'custom'
  if (source === 'custom' || source === 'value') {
    return config.systemValue ?? config.value
  }

  let sourceData = getSourceData(source, designContext, data)
  return objectPath.get(
    sourceData,
    config.systemKey || config.key || config.sourceKey || '',
  )
}

const getRuleSweetAlertTitleTokenValue = (
  option = {},
  designContext = {},
  data = {},
) => {
  if (option.__isNew__) return option.value || ''

  let { jsonForm = {}, user = {}, urlParams = {} } = designContext
  let formData = data.formData || jsonForm.formData || {}
  let datasourceValue = getDatasourceValue(designContext, data)
  let key = option.key || option.value || option.name || ''
  let value = option.value || key
  let layout = option.layout || option.groupType || ''

  if (layout === 'datasources') {
    return objectPath.get(datasourceValue, key) ?? value
  }
  if (layout === 'response') {
    return objectPath.get(data.response || {}, key) ?? value
  }
  if (layout === 'urlParam' || layout === 'urlParams') {
    return objectPath.get(data.urlParams || urlParams || {}, key) ?? value
  }
  if (layout === 'user') {
    return objectPath.get(user || {}, key) ?? value
  }

  return (
    objectPath.get(formData, key) ??
    objectPath.get(formData, value) ??
    objectPath.get(datasourceValue, key) ??
    value
  )
}

const getRuleSweetAlertDateTimeValue = (
  value,
  titleConfig = {},
  designContext = {},
) => {
  if (value === '' || value === undefined || value === null) return value

  let { formatDate, formatTime, formatDateTime, dateFormat, timeFormat } =
    designContext
  let { dateTimeType, format, custom = {} } = titleConfig

  if (format === 'system') {
    if (dateTimeType === 'date') {
      if (formatDate) return formatDate(value)
      if (formatDateTime && dateFormat) return formatDateTime(value, dateFormat)
    }
    if (dateTimeType === 'time') {
      if (formatTime) return formatTime(value)
      if (formatDateTime && timeFormat) return formatDateTime(value, timeFormat)
    }
    if (dateTimeType === 'dateTime' && formatDateTime) {
      return formatDateTime(value)
    }
  }

  if (format === 'custom') {
    let customDateFormat = custom.dateFormat
    let customTimeFormat = custom.timeFormat
    if (dateTimeType === 'date') {
      if (formatDate) return formatDate(value, customDateFormat)
      if (formatDateTime && customDateFormat) {
        return formatDateTime(value, customDateFormat)
      }
    }
    if (dateTimeType === 'time') {
      if (formatTime) return formatTime(value, customTimeFormat)
      if (formatDateTime && customTimeFormat) {
        return formatDateTime(value, customTimeFormat)
      }
    }
    if (dateTimeType === 'dateTime') {
      if (formatDateTime) {
        return formatDateTime(value, `${customDateFormat} ${customTimeFormat}`)
      }
    }
  }

  return value
}

const getRuleSweetAlertTitleTokenHtml = (
  option = {},
  titleConfig = {},
  designContext = {},
  data = {},
) => {
  if (option.__isNewLine__) return '<br/>'

  let value = getRuleSweetAlertTitleTokenValue(option, designContext, data)
  if (titleConfig.type === 'dateTime' && !option.__isNew__) {
    value = getRuleSweetAlertDateTimeValue(value, titleConfig, designContext)
  }
  return escapeHtml(value).replace(/\n/g, '<br/>')
}

const createRuleSweetAlertTitleHtml = (
  alertConfig = {},
  designContext = {},
  data = {},
) => {
  let { titleValue = [] } = alertConfig
  if (!Array.isArray(titleValue) || titleValue.length === 0) return ''

  let html = []
  titleValue.forEach((titleConfig = {}) => {
    let titleList = Array.isArray(titleConfig.title) ? titleConfig.title : []
    if (titleList.length === 0) return

    let htmlString = titleList
      .map((option) => {
        return getRuleSweetAlertTitleTokenHtml(
          option,
          titleConfig,
          designContext,
          data,
        )
      })
      .join('')
    let style = [`font-size:${escapeCssValue(titleConfig.fontSize || '16px')}`]

    if (titleConfig.fontColor) {
      style.push(`color:${escapeCssValue(titleConfig.fontColor)}`)
    }

    html.push(
      `<p class="mb-1 ${
        titleConfig.fontBold ? 'font-weight-bold' : ''
      }" style="${style.join(';')}">${htmlString}</p>`,
    )
  })

  return html.join('')
}

const replacePathParams = (url, pathParams = [], designContext = {}, data = {}) => {
  let nextUrl = url

  pathParams.forEach((paramConfig = {}) => {
    let paramValue = getConfigValue(paramConfig, designContext, data)
    if (paramValue === undefined || paramValue === null) paramValue = ''

    nextUrl = nextUrl.replace(
      `:${paramConfig.param}`,
      encodeURIComponent(paramValue),
    )
  })

  return nextUrl
}

const appendQueryParams = (
  url,
  queryParams = [],
  designContext = {},
  data = {},
) => {
  let queryString = []

  queryParams.forEach((paramConfig = {}) => {
    let paramValue = getConfigValue(paramConfig, designContext, data)
    if (paramValue === undefined || paramValue === null) return

    queryString.push(
      `${paramConfig.param}=${encodeURIComponent(paramValue)}`,
    )
  })

  if (queryString.length === 0) return url

  let connect = url.indexOf('?') > -1 ? '&' : '?'
  return url ? url + connect + queryString.join('&') : url
}

const createGoToUrl = (button = {}, designContext = {}, data = {}) => {
  let linkType = getButtonLinkType(button)
  let formCode = button.formCode || button.target || ''
  let url = button.pagePath || button.url || ''

  if (linkType === 'page') {
    url = url || `/display/page/${formCode}`
  } else if (linkType === 'form') {
    url = url || `/display/form/${formCode}`
  } else if (linkType === 'print') {
    url = url || PRINT_PATH
  }

  url = replacePathParams(url, button.pathParams || [], designContext, data)
  url = appendQueryParams(url, button.queryParams || [], designContext, data)

  return url
}

const goToRuleSweetAlertUrl = (
  button = {},
  designContext = {},
  data = {},
) => {
  let url = createGoToUrl(button, designContext, data)
  if (!url) return

  let target = button.openNewTab ? '_blank' : '_self'
  if (url.startsWith('http')) {
    window.open(url, target)
  } else if (url.startsWith('/')) {
    window.open(ROUTE_HIS + url, target)
  } else {
    window.open(`${ROUTE_HIS}/${url}`, target)
  }
}

const setRuleSweetAlertProps = (
  button = {},
  designContext = {},
  data = {},
) => {
  let { jsonForm = {}, setDatasources } = designContext
  let props = Array.isArray(button.propAlert) ? button.propAlert : []
  if (props.length === 0) return {}

  let datasourceValue = getDatasourceValue(designContext, data)
  let propAlertData = objectPath.get(datasourceValue, 'propAlert') || {}
  let datasources = objectPath.get(jsonForm, 'datasources') || {}
  let propAlertSource = objectPath.get(datasources, 'propAlert') || {}
  let sourceData = objectPath.get(propAlertSource, 'data') || {}
  let sourceKeyList = objectPath.get(propAlertSource, 'key') || []
  let propData = {}

  props.forEach((propConfig = {}) => {
    if (!propConfig.prop) return

    let propValue = getConfigValue(propConfig, designContext, data)
    objectPath.set(propData, propConfig.prop, propValue)
    objectPath.set(propAlertData, propConfig.prop, propValue)
    sourceData[propConfig.prop] = true
    if (!sourceKeyList.includes(propConfig.prop)) {
      sourceKeyList.push(propConfig.prop)
    }
  })

  objectPath.set(datasourceValue, 'propAlert', propAlertData)
  objectPath.set(designContext, '@datasource', datasourceValue)
  if (data.datasource) {
    objectPath.set(data.datasource, 'propAlert', propAlertData)
  }
  if (data.datasources) {
    objectPath.set(data.datasources, 'propAlert', propAlertData)
  }

  objectPath.set(datasources, 'propAlert', {
    ...propAlertSource,
    name: propAlertSource.name || 'Prop Alert',
    data: sourceData,
    key: sourceKeyList,
  })
  if (setDatasources) setDatasources(datasources)

  return propData
}

const runRuleSweetAlertButtonAction = (
  button = {},
  designContext = {},
  data = {},
) => {
  switch (button.actionType) {
    case 'goTo':
      return goToRuleSweetAlertUrl(button, designContext, data)
    case 'closeAlert':
    default:
      return setRuleSweetAlertProps(button, designContext, data)
  }
}

const createSwalOptions = (
  alertConfig = {},
  confirmButton = {},
  cancelButton = {},
  designContext = {},
  data = {},
) => {
  let titleHtml = createRuleSweetAlertTitleHtml(
    alertConfig,
    designContext,
    data,
  )
  let swalOptions = {
    icon: alertConfig.icon || 'success',
    html: titleHtml || '',
    reverseButtons: Boolean(alertConfig.reverseButtons),
    showConfirmButton: Boolean(confirmButton.enabled),
    showCancelButton: Boolean(cancelButton.enabled),
    confirmButtonText: getButtonText(confirmButton),
    cancelButtonText: getButtonText(cancelButton),
  }

  if (confirmButton.color) {
    swalOptions.confirmButtonColor = confirmButton.color
  }
  if (cancelButton.color) {
    swalOptions.cancelButtonColor = cancelButton.color
  }

  return swalOptions
}

export const onRuleSweetAlert = async (
  designContext = {},
  action = {},
  data = {},
) => {
  let successModal = getRuleSweetAlertConfig(action)
  let alertConfig = getRuleSweetAlertAlert(successModal)
  let confirmButton = getRuleSweetAlertButton(successModal, 'confirmButton')
  let cancelButton = getRuleSweetAlertButton(successModal, 'cancelButton')
  let result = await Swal.fire(
    createSwalOptions(
      alertConfig,
      confirmButton,
      cancelButton,
      designContext,
      data,
    ),
  )

  if (result.isConfirmed && confirmButton.enabled) {
    return runRuleSweetAlertButtonAction(confirmButton, designContext, data)
  }

  if (
    result.dismiss === Swal.DismissReason.cancel &&
    cancelButton.enabled
  ) {
    return runRuleSweetAlertButtonAction(cancelButton, designContext, data)
  }

  return result
}
